/**
 * File upload utility with HEIC conversion support
 * Uses Vercel Blob client uploads for files of any size
 * HEIC files are converted server-side using heic-convert for reliability
 */
import { upload } from '@vercel/blob/client';

/**
 * Check if a file is HEIC/HEIF format
 * Checks extension first as MIME type is often incorrect or empty for HEIC files
 * @param {File} file - The file to check
 * @returns {boolean}
 */
function isHeicFile(file) {
  if (!file || !file.name) return false;
  
  const extension = file.name.split('.').pop().toLowerCase();
  const mimeType = (file.type || '').toLowerCase();
  
  // Check extension first (most reliable for HEIC files)
  if (extension === 'heic' || extension === 'heif') {
    return true;
  }
  
  // Also check MIME type (though often empty or wrong for HEIC)
  if (mimeType === 'image/heic' || mimeType === 'image/heif') {
    return true;
  }
  
  return false;
}

// Cloudinary fetch (used for all image delivery) rejects sources over 10MB,
// so large photos are downscaled in the browser before upload.
const MAX_IMAGE_DIMENSION = 2560;
const RESIZE_THRESHOLD_BYTES = 1.5 * 1024 * 1024;
const JPEG_QUALITY = 0.85;

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to decode image'));
    };
    img.src = objectUrl;
  });
}

/**
 * Downscale large JPEG/PNG/WebP/HEIC photos to fit within MAX_IMAGE_DIMENSION.
 * HEIC is re-encoded as JPEG when the browser can decode it (Safari); otherwise
 * the original file is returned and converted server-side.
 * @param {File} file
 * @returns {Promise<File>} - The resized file, or the original if unchanged
 */
async function downscaleImage(file) {
  if (typeof document === 'undefined' || !file) return file;

  const extension = (file.name.split('.').pop() || '').toLowerCase();
  const mimeType = (file.type || '').toLowerCase();
  const isHeic = isHeicFile(file);
  const isPng = mimeType === 'image/png' || extension === 'png';
  const isResizable = isHeic || isPng ||
    ['image/jpeg', 'image/jpg', 'image/webp'].includes(mimeType) ||
    ['jpg', 'jpeg', 'webp'].includes(extension);

  if (!isResizable || (!isHeic && file.size <= RESIZE_THRESHOLD_BYTES)) return file;

  try {
    const img = await loadImageElement(file);
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.round(img.naturalWidth * scale);
    const height = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const outputType = isPng ? 'image/png' : 'image/jpeg';
    if (outputType === 'image/jpeg') {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, outputType, JPEG_QUALITY));
    if (!blob || (!isHeic && blob.size >= file.size)) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.${isPng ? 'png' : 'jpg'}`, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.warn('Image downscale skipped:', error);
    return file;
  }
}

/**
 * Backup upload provider using Cloudinary signed upload.
 * Automatically invoked if Vercel Blob upload or HEIC conversion fails
 * (e.g. due to a storage/billing quota limit or service hiccup).
 */
async function uploadViaCloudinaryFallback(file, options = {}) {
  let token = null;
  let email = '';
  if (typeof window !== 'undefined') {
    try {
      token = window.localStorage.getItem('postaway_auth_token') || null;
      const params = new URLSearchParams(window.location.search || '');
      email = params.get('email') || window.localStorage.getItem('postaway_email') || '';
    } catch (_) {}
  }

  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();
  const isVideo = mime.startsWith('video/') || ['mp4', 'mov', 'webm'].includes(ext);
  const resourceType = isVideo ? 'video' : 'image';

  const signRes = await fetch('/api/cloudinary/sign', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      kind: 'fallback_upload',
      resourceType,
      email,
    }),
  });

  const sigData = await signRes.json();
  if (!signRes.ok || !sigData.ok) {
    throw new Error(sigData.error || 'Fallback upload signature failed');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', sigData.apiKey);
  formData.append('timestamp', sigData.timestamp);
  formData.append('signature', sigData.signature);
  formData.append('public_id', sigData.publicId);
  if (sigData.eager) {
    formData.append('eager', sigData.eager);
  }
  formData.append('overwrite', 'true');
  formData.append('invalidate', 'true');

  const uploadRes = await fetch(
    `https://api.cloudinary.com/v1_1/${sigData.cloudName}/${resourceType}/upload`,
    {
      method: 'POST',
      body: formData,
    }
  );

  const uploaded = await uploadRes.json();
  if (!uploadRes.ok || !uploaded.secure_url) {
    throw new Error(uploaded?.error?.message || 'Cloudinary fallback upload failed');
  }

  // Prefer the eager JPG derivative for HEIC/HEIF or large images so all browsers render it
  const eagerJpg = Array.isArray(uploaded.eager)
    ? uploaded.eager.find((item) => item?.secure_url)?.secure_url
    : null;

  if (options.onProgress) {
    options.onProgress('Complete');
  }

  return eagerJpg || uploaded.secure_url;
}

/**
 * Upload a file to Vercel Blob with automatic HEIC conversion and
 * automatic failover to Cloudinary if Vercel Blob or HEIC conversion fails.
 * @param {File} rawFile - The file to upload
 * @param {Object} options - Upload options
 * @param {Function} options.onProgress - Progress callback (optional)
 * @returns {Promise<string>} - The URL of the uploaded file
 */
export async function uploadFile(rawFile, options = {}) {
  const file = await downscaleImage(rawFile);
  const isHeic = isHeicFile(file);

  try {
    // Upload to Vercel Blob (works for all file sizes)
    if (options.onProgress) {
      options.onProgress('Uploading...');
    }

    const blob = await upload(file.name, file, {
      access: 'public',
      handleUploadUrl: '/api/upload',
    });

    // If it's HEIC, convert it server-side
    if (isHeic) {
      if (options.onProgress) {
        options.onProgress('Converting...');
      }

      const conversionResponse = await fetch('/api/convert-heic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          blobUrl: blob.url,
          deleteOriginal: true // Clean up the original HEIC file
        }),
      });

      const conversionData = await conversionResponse.json();

      if (conversionData.ok && conversionData.url) {
        console.log('HEIC converted successfully:', conversionData.url);
        if (options.onProgress) {
          options.onProgress('Complete');
        }
        return conversionData.url; // Return the converted JPEG URL
      }

      // Never return an unconverted .heic URL (browsers other than Safari can't render it);
      // fall through to Cloudinary fallback which natively transcodes HEIC to JPG.
      throw new Error(conversionData.error || 'HEIC conversion failed');
    }

    if (options.onProgress) {
      options.onProgress('Complete');
    }

    return blob.url;
  } catch (primaryError) {
    console.warn('Primary upload failed, attempting Cloudinary fallback:', primaryError);
    try {
      return await uploadViaCloudinaryFallback(file, options);
    } catch (fallbackError) {
      console.error('Both primary and fallback uploads failed:', primaryError, fallbackError);
      throw new Error(primaryError.message || 'Failed to upload file');
    }
  }
}

/**
 * Validate file before upload
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSize - Maximum file size in bytes (default: 50MB)
 * @param {string[]} options.allowedTypes - Allowed MIME types
 * @returns {Object} - Validation result
 */
export function validateFile(file, options = {}) {
  const maxSize = options.maxSize || 50 * 1024 * 1024; // 50MB default
  const allowedTypes = options.allowedTypes || [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/heic',
    'image/heif',
    'application/pdf',
    'video/mp4',
    'video/quicktime',
    'video/webm',
  ];

  if (!file) {
    return {
      isValid: false,
      error: 'No file selected',
    };
  }

  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    return {
      isValid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`,
    };
  }

  // Check file type by extension and MIME type
  const extension = file.name.split('.').pop().toLowerCase();
  const mimeType = file.type.toLowerCase();
  
  const isValidType = allowedTypes.some(type => {
    if (type.startsWith('image/') && mimeType.startsWith('image/')) {
      return true;
    }
    if (type.startsWith('video/') && mimeType.startsWith('video/')) {
      return true;
    }
    return mimeType === type;
  }) || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'pdf', 'mp4', 'mov', 'webm'].includes(extension);

  if (!isValidType) {
    return {
      isValid: false,
      error: 'File type not supported. Please use JPG, PNG, GIF, WEBP, HEIC, PDF, MP4, MOV, or WEBM.',
    };
  }

  return {
    isValid: true,
  };
}
